import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Observable, catchError, from, of, switchMap, throwError } from 'rxjs';
import { AlertService } from '@core/services/alert.service/alert.service';
import { API_ENDPOINTS } from '../../api-endpoints';
import { ApiErrorBody } from '../../models/api-error.model';
import { ERROR_CODES, SERVER_ERROR_PREFIX, isKnownErrorCode } from '../../models/error-codes';
import { HTTP_STATUS, isSuccessStatus } from '../../models/http-status';
import { SKIP_ERROR_ALERT } from '../../http-context.tokens';

/**
 * Endpoints whose errors are handled entirely by the caller — the interceptor
 * does not even look at them (legacy `NO_INTERCEPT_URL`).
 *
 * **Wpis jest dopuszczalny wyłącznie wtedy, gdy po drugiej stronie ktoś ten błąd
 * naprawdę pokazuje.** Inaczej lista nie wycisza alertu, tylko gubi błąd: żądanie
 * przelatuje bez `catchError`, a operator nie dowiaduje się o niczym.
 *
 * Tak było z `products.additionalOther` (usunięty 02-09-2026): zapis usługi
 * dodatkowej wracał z 400 — cena ponad katalogową — a `ProductModal.save()` w gałęzi
 * `error` gasi wyłącznie spinner. Klikanie „Zapisz" nie dawało żadnego znaku życia.
 *
 * I tak samo z `products.mainServiceFees` (usunięty 09-09-2026). Wpis stał tu
 * z uzasadnieniem, że `installation-page` czyta adres przez `apiResource` i renderuje
 * własny stan błędu — a szablon tego ekranu ma wyłącznie gałąź `isLoading()`. Odczyt
 * wręcz prosił o alert wprost (`skipErrorAlert: false`), tyle że lista działa **przed**
 * sprawdzeniem `SKIP_ERROR_ALERT`, więc flaga była martwa. Odrzucony zapis opłaty
 * (`MAIN_SERVICE_FEE_DUPLICATE`) ginął dokładnie jak cena usługi dodatkowej.
 *
 * Dopasowanie idzie przez `url.includes`, więc zdjęcie wpisu odsłania też adres
 * pakietowy i zapis definicji SORT — świadomie, bo tam również nikt tych błędów
 * nie pokazuje.
 *
 * Ekran, który potrzebuje ciszy tylko przy odczycie, ma do tego `skipErrorAlert`
 * przy swoim wywołaniu — nie trzeba wtedy wyciszać całego adresu razem z zapisem.
 */
const UNHANDLED_ENDPOINTS: readonly string[] = [API_ENDPOINTS.images.byId('')];

/**
 * Endpoints that may fail without bothering the user with an alert — the
 * screens behind them show the failure themselves (legacy `NO_ALERT_RESTS`).
 */
const SILENT_ENDPOINTS: readonly string[] = [
  API_ENDPOINTS.languageBundles,
  API_ENDPOINTS.auth.login,
  '/public-pages',
];

/**
 * Global API error handling: turns a failed response into one translated
 * message and shows it as a danger snackbar (the jmdi behaviour — the starter
 * used to open a modal).
 *
 * Message resolution follows the legacy `responseBodyError.interceptor`:
 * the status decides which fallback applies, and a `code`/`message` from the
 * body is only used when it is a code we know (`ERROR_CODES`), so an unknown
 * backend identifier never reaches the user.
 *
 * 401 handling stays in `authTokenInterceptor`, which sits closer to the
 * request — an error only reaches this interceptor once the refresh has
 * already failed.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (UNHANDLED_ENDPOINTS.some((endpoint) => req.url.includes(endpoint))) {
    return next(req);
  }

  // Services are resolved lazily, only when an error actually occurs.
  // Injecting TranslateService eagerly here would create a circular dependency:
  // the translation files themselves are loaded through this interceptor chain
  // while TranslateService is still being constructed (NG0200).
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // A 2xx that merely failed to parse (empty body) is not a failure.
      if (isSuccessStatus(error.status)) {
        return of(
          new HttpResponse({
            body: null,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          }),
        );
      }

      return readErrorBody(error).pipe(
        switchMap((body) => {
          if (!req.context.get(SKIP_ERROR_ALERT) && !isSilent(req.url)) {
            injector.get(AlertService).danger(resolveMessageKey(error.status, body));
          }
          return throwError(() => error);
        }),
      );
    }),
  );
};

/** Blob responses (file downloads) carry their JSON error inside the blob. */
function readErrorBody(error: HttpErrorResponse): Observable<ApiErrorBody | null> {
  if (error.error instanceof Blob) {
    return from(
      error.error
        .text()
        .then((text) => JSON.parse(text) as ApiErrorBody)
        .catch(() => null),
    );
  }
  return of(
    typeof error.error === 'object' && error.error !== null
      ? (error.error as ApiErrorBody)
      : null,
  );
}

function isSilent(url: string): boolean {
  return SILENT_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/** Maps a status + error body onto one `serverError.*` translation key. */
function resolveMessageKey(status: number, body: ApiErrorBody | null): string {
  const code = body?.code;
  const message = body?.message;

  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return key(isKnownErrorCode(code) ? code : ERROR_CODES.PERMISSION_EXCEPTION);
    case HTTP_STATUS.UNAUTHORIZED:
      return key(isKnownErrorCode(code) ? code : ERROR_CODES.UNAUTHORIZED);
    case HTTP_STATUS.FORBIDDEN:
      return key(isKnownErrorCode(code) ? code : ERROR_CODES.PERMISSION_EXCEPTION);
    case HTTP_STATUS.NOT_FOUND:
      if (isKnownErrorCode(code)) {
        return key(code);
      }
      return key(isKnownErrorCode(message) ? message : ERROR_CODES.NOT_FOUND_ERROR);
    case HTTP_STATUS.CONFLICT:
      if (code === ERROR_CODES.CONFLICT) {
        return key(isKnownErrorCode(message) ? message : ERROR_CODES.COMMUNICATION_ERROR);
      }
      return key(isKnownErrorCode(code) ? code : ERROR_CODES.COMMUNICATION_ERROR);
    case HTTP_STATUS.IM_A_TEAPOT:
      if (isKnownErrorCode(code)) {
        return key(code);
      }
      return key(isKnownErrorCode(message) ? message : ERROR_CODES.COMMUNICATION_ERROR);
    default:
      return key(ERROR_CODES.COMMUNICATION_ERROR);
  }
}

function key(code: string): string {
  return `${SERVER_ERROR_PREFIX}${code}`;
}
